import React from 'react';
import { Progress, Tag } from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, LoadingOutlined, ClockCircleOutlined,
} from '@ant-design/icons';

// Sequential phase order the backend runs the import in (see
// order_genie_service/services/customer_directory/import_job.py) -- used to
// compute a single overall percentage across phases that each have their
// own (processed, total), and to know which phases are "done" vs "pending"
// relative to whichever phase is currently running.
const PHASES = [
  { key: 'customers', label: 'Customers' },
  { key: 'billtos', label: 'Bill-To Addresses' },
  { key: 'addresses', label: 'Ship-To Addresses' },
  { key: 'contacts', label: 'Contacts' },
];

export const STATUS_META = {
  queued: { color: 'default', label: 'Queued', icon: <ClockCircleOutlined /> },
  running: { color: 'processing', label: 'Importing…', icon: <LoadingOutlined /> },
  completed: { color: 'success', label: 'Completed', icon: <CheckCircleFilled /> },
  failed: { color: 'error', label: 'Failed', icon: <CloseCircleFilled /> },
};

// Each entity phase is weighted by its own row count (a 500-row customer
// phase should move the overall bar more than a 3-row contact phase);
// indexing has no "rows" of its own so it gets a small flat weight just so
// the bar visibly ticks forward during it instead of sitting at 100% early.
const INDEXING_WEIGHT = 1;

function phaseStatus(phaseKey, currentPhase, status) {
  const order = ['customers', 'billtos', 'addresses', 'contacts', 'indexing', 'done'];
  const currentIndex = order.indexOf(currentPhase);
  const thisIndex = order.indexOf(phaseKey);
  if (status === 'failed' && thisIndex === currentIndex) return 'failed';
  if (status === 'completed' || thisIndex < currentIndex) return 'done';
  if (thisIndex === currentIndex) return 'active';
  return 'pending';
}

export function computeOverallPercent(progress, currentPhase, status) {
  if (status === 'completed') return 100;
  const totals = progress?.totals || {};
  const weights = { ...totals, indexing: INDEXING_WEIGHT };
  const totalWeight = PHASES.reduce((sum, p) => sum + (totals[p.key] || 0), 0) + INDEXING_WEIGHT;
  if (!totalWeight) return status === 'running' ? 5 : 0;

  let done = 0;
  const order = ['customers', 'billtos', 'addresses', 'contacts', 'indexing'];
  const currentIndex = order.indexOf(currentPhase);
  order.forEach((key, idx) => {
    if (idx < currentIndex) done += weights[key] || 0;
  });
  if (currentIndex >= 0 && progress?.current?.phase === currentPhase) {
    const { processed = 0, total = 0 } = progress.current;
    done += total ? (processed / total) * (weights[currentPhase] || 0) : 0;
  }
  return Math.max(1, Math.min(100, Math.round((done / totalWeight) * 100)));
}

export default function CustomerImportProgressPanel({ status, phase, progress, error }) {
  const meta = STATUS_META[status] || STATUS_META.queued;
  const overallPercent = computeOverallPercent(progress, phase, status);
  const overallStrokeColor = status === 'failed'
    ? '#ff4d4f'
    : { '0%': '#3b6fdb', '100%': '#22c55e' };

  return (
    <div className="ci-card ci-card-flush p-3 p-md-4 mb-3 ci-import-progress">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <h5 className="mb-0">Import Progress</h5>
        <Tag color={meta.color} icon={meta.icon} style={{ fontSize: 13, padding: '2px 10px' }}>
          {meta.label}
        </Tag>
      </div>

      <Progress
        percent={overallPercent}
        status={status === 'failed' ? 'exception' : (status === 'running' || status === 'queued' ? 'active' : 'success')}
        strokeColor={overallStrokeColor}
        strokeWidth={10}
      />

      {error && (
        <div className="alert alert-danger py-2 px-3 mt-2 mb-0 small">{error}</div>
      )}

      <div className="row g-3 mt-1">
        {PHASES.map(({ key, label }) => {
          const st = phaseStatus(key, phase, status);
          const total = progress?.totals?.[key] ?? 0;
          const isCurrent = progress?.current?.phase === key;
          const processed = isCurrent ? (progress.current.processed ?? 0) : (st === 'done' ? total : 0);
          const percent = total ? Math.round((processed / total) * 100) : (st === 'done' ? 100 : 0);
          return (
            <div className="col-6 col-md-3" key={key}>
              <div className={`ci-import-phase-card ci-import-phase-card--${st}`}>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="ci-import-phase-label">{label}</span>
                  {st === 'done' && <CheckCircleFilled style={{ color: 'var(--ci-success, #22c55e)' }} />}
                  {st === 'active' && <LoadingOutlined style={{ color: 'var(--ci-accent)' }} spin />}
                </div>
                <Progress
                  percent={percent}
                  size="small"
                  showInfo={false}
                  status={st === 'failed' ? 'exception' : (st === 'active' ? 'active' : 'success')}
                  strokeColor={st === 'pending' ? '#d9d9d9' : undefined}
                />
                <div className="ci-import-phase-count">
                  {total > 0 ? `${processed} / ${total}` : 'no rows mapped'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {phase === 'indexing' && status === 'running' && (
        <div className="small text-muted mt-2">Indexing into search…</div>
      )}
    </div>
  );
}
